/**
 * Detecting when *our own* Content-Security-Policy blocked a request.
 *
 * The problem this solves is a genuinely unanswerable bug report. When CSP
 * refuses a fetch, the promise rejects with a bare `TypeError: Failed to fetch`
 * — byte-for-byte identical to the rejection you get from DNS failure, an
 * offline machine, or a CORS refusal. The spec deliberately withholds the
 * reason, because leaking it would be a side channel. So a visitor sees
 * "something went wrong", we see nothing, and the actual cause (a hostname
 * missing from `connect-src` in `infra/lib/site-stack.ts`) is invisible from
 * both ends.
 *
 * This is not hypothetical here. The Hub redirects payload reads to its storage
 * CDN, and those hostnames have moved before — the Xet migration puts some
 * repos behind a different registrable domain entirely. A `connect-src` that
 * covers the API host but not the redirect target produces the worst possible
 * shape of failure: **the tensor list works and every tensor read fails**,
 * because the header comes from one host and the payload from another.
 *
 * The reliable signal is `securitypolicyviolation`, which the document fires
 * whenever CSP blocks anything, carrying the blocked URI and the directive that
 * did it. Listening for it costs nothing and turns "failed to fetch" into "the
 * site's own policy refused cdn-xyz.example — that is our bug, not yours."
 *
 * Deliberately NOT reported anywhere. This exists to put an accurate sentence
 * on screen, not to phone home; there is no telemetry in this app.
 */

export interface CspViolation {
  /** The URI CSP refused. May be an origin only, or the pre-redirect URL. */
  readonly blockedUri: string;
  /** e.g. `connect-src`. */
  readonly directive: string;
  readonly at: number;
}

/**
 * How recently a violation must have fired to be considered the cause of a
 * given fetch rejection. Generous, because the violation event and the promise
 * rejection are dispatched on different paths and their ordering is not
 * guaranteed; a false attribution here is far cheaper than a missed one, since
 * the alternative message is the useless "failed to fetch".
 */
const CORRELATION_WINDOW_MS = 3000;

/** Small ring buffer — we only ever look at the recent past. */
const recent: CspViolation[] = [];
const MAX_RETAINED = 16;

let installed = false;

/**
 * Start listening. Idempotent, and safe to call in a non-browser context (the
 * unit tests import this module's pure half without a `document`).
 */
export function installCspWatch(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;

  document.addEventListener("securitypolicyviolation", (event) => {
    recent.push({
      blockedUri: event.blockedURI,
      // `effectiveDirective` is the specific directive that matched;
      // `violatedDirective` is the whole source-list string. The former is what
      // a human wants to read.
      directive: event.effectiveDirective || event.violatedDirective,
      at: Date.now(),
    });
    if (recent.length > MAX_RETAINED) recent.shift();
  });
}

/**
 * Did CSP block a connection just now?
 *
 * `url` is matched loosely — by origin, and only when the violation carries a
 * usable URI at all. CSP reports `blockedURI` inconsistently for redirects
 * (browsers may report the pre-redirect URL, the final target, or bare
 * `"connect-src"`-style placeholders), and for cross-origin cases it is often
 * stripped to just the origin. Matching strictly would miss exactly the
 * redirect case this exists for, so any recent `connect-src` violation counts
 * as a hit when the URI cannot be compared.
 */
export function recentConnectViolation(url: string): CspViolation | undefined {
  const cutoff = Date.now() - CORRELATION_WINDOW_MS;
  let targetOrigin: string | undefined;
  try {
    targetOrigin = new URL(url).origin;
  } catch {
    targetOrigin = undefined;
  }

  for (let i = recent.length - 1; i >= 0; i--) {
    const violation = recent[i];
    if (!violation || violation.at < cutoff) continue;
    if (!violation.directive.startsWith("connect-src")) continue;

    if (targetOrigin === undefined) return violation;
    let blockedOrigin: string | undefined;
    try {
      blockedOrigin = new URL(violation.blockedUri).origin;
    } catch {
      // Not a parseable URL — a placeholder or a bare origin. Can't compare, so
      // treat it as a match rather than dropping the only useful evidence.
      return violation;
    }
    if (blockedOrigin === targetOrigin) return violation;
    // A connect-src violation fired within the window but for a different
    // origin — almost certainly the redirect target of this very request, which
    // is the case we most need to catch.
    return violation;
  }
  return undefined;
}

/** Thrown when the site's own policy, not the network, refused a request. */
export class CspBlockedError extends Error {
  readonly blockedUri: string;
  readonly directive: string;

  constructor(requestedUrl: string, violation: CspViolation) {
    const host =
      safeHost(violation.blockedUri) ?? safeHost(requestedUrl) ?? "that host";
    super(
      `This site's own Content-Security-Policy blocked a request to ${host} (${violation.directive}). That is a bug in TensorLens, not a problem with the Hub or your network — the Hub redirects model downloads to storage hosts that our policy has to list explicitly, and it appears one is missing. Please report it with this hostname.`,
    );
    this.name = "CspBlockedError";
    this.blockedUri = violation.blockedUri;
    this.directive = violation.directive;
  }
}

function safeHost(value: string): string | undefined {
  try {
    return new URL(value).host;
  } catch {
    return undefined;
  }
}

/** Test seam: drop retained violations. */
export function resetCspWatchForTests(): void {
  recent.length = 0;
}

/** Test seam: inject a violation without a real CSP. */
export function recordViolationForTests(violation: CspViolation): void {
  recent.push(violation);
  if (recent.length > MAX_RETAINED) recent.shift();
}
